import { isDrawn, partialSetsOf } from './scoring-match-draft';
import { matchPoints } from './scoring-match-points';
import type { MatchItem, ScoringSystem } from './scoring-types';

export type PlayerSide = 1 | 2;

export function normalizeSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function filterMatches(matches: MatchItem[], query: string): MatchItem[] {
  const needle = normalizeSearch(query);

  if (needle === '') {
    return matches;
  }

  return matches.filter((match) =>
    [...match.side1, ...match.side2].some((player) =>
      normalizeSearch(player.name).includes(needle),
    ),
  );
}

export function sideOfPlayer(match: MatchItem, leaguePlayerId: number): PlayerSide | null {
  if (match.side1.some((player) => player.memberId === leaguePlayerId)) {
    return 1;
  }

  if (match.side2.some((player) => player.memberId === leaguePlayerId)) {
    return 2;
  }

  return null;
}

export function matchesOfPlayer(matches: MatchItem[], leaguePlayerId: number): MatchItem[] {
  return matches.filter((match) => sideOfPlayer(match, leaguePlayerId) !== null);
}

export function opponentName(match: MatchItem, leaguePlayerId: number): string {
  if (sideOfPlayer(match, leaguePlayerId) === 2) {
    return match.player1Name;
  }

  return match.player2Name;
}

export function partnerName(match: MatchItem, leaguePlayerId: number): string | null {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null) {
    return null;
  }

  const partner = (side === 1 ? match.side1 : match.side2).find(
    (player) => player.memberId !== leaguePlayerId,
  );

  return partner?.name ?? null;
}

export function opponentPlayerIds(match: MatchItem, leaguePlayerId: number): number[] {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null) {
    return [];
  }

  return (side === 1 ? match.side2 : match.side1).flatMap((player) => player.playerIds ?? []);
}

export function partnerPlayerIds(match: MatchItem, leaguePlayerId: number): number[] {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null) {
    return [];
  }

  const partner = (side === 1 ? match.side1 : match.side2).find(
    (player) => player.memberId !== leaguePlayerId,
  );

  return partner?.playerIds ?? [];
}

export function opponentRating(match: MatchItem, leaguePlayerId: number): number | null {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null || !match.rating) {
    return null;
  }

  return side === 1 ? match.rating.side2.rating : match.rating.side1.rating;
}

export type PlayerOutcome = 'win' | 'loss' | 'draw';

const OUTCOME_WORD: Record<PlayerOutcome, string> = {
  win: 'wygrana',
  loss: 'porażka',
  draw: 'remis',
};

export function playerOutcome(match: MatchItem, leaguePlayerId: number): PlayerOutcome | null {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null || (match.winnerSide === null && !isDrawn(match))) {
    return null;
  }

  if (match.winnerSide === null) {
    return 'draw';
  }

  return (match.winnerSide === 'SIDE1' ? 1 : 2) === side ? 'win' : 'loss';
}

export function playerResultLabel(match: MatchItem, leaguePlayerId: number): string {
  const side = sideOfPlayer(match, leaguePlayerId);
  const outcome = playerOutcome(match, leaguePlayerId);

  if (side === null) {
    return 'bez wyniku';
  }

  if (outcome === null) {
    const partial = partialSetsOf(match)
      .map((set) => (side === 1 ? `${set.p1}:${set.p2}` : `${set.p2}:${set.p1}`))
      .join('  ');

    return partial === '' ? 'bez wyniku' : `w trakcie ${partial}`;
  }

  const sets = match.sets
    .map((set) => (side === 1 ? `${set.p1}:${set.p2}` : `${set.p2}:${set.p1}`))
    .join('  ');

  return `${OUTCOME_WORD[outcome]} ${sets}`.trim();
}

export function playerPointsLabel(
  match: MatchItem,
  scoring: ScoringSystem | undefined,
  leaguePlayerId: number,
): string | null {
  const side = sideOfPlayer(match, leaguePlayerId);
  const awarded = matchPoints(scoring, match.sets);

  if (side === null || awarded === null) {
    return null;
  }

  return `${side === 1 ? awarded.side1 : awarded.side2} pkt`;
}
